<template>
    <OverrideReportStatusModal :component-id="transformerId" :component-type="ComponentType.Transformer">
        <ConditionStatusOverrideModal :component-id="transformerId" :component-type="ComponentType.Transformer">
            <ConditionStatusCommentModal :component-id="transformerId" :component-type="ComponentType.Transformer">
                <ComponentCommentModal :component-id="transformerId" :component-type="ComponentType.Transformer">
                    <SystemStatusDescriptionModal>
                        <ConditionStatusViewRecommendationModal>
                            <ConditionStatusEditRecommendationModal :component-id="transformerId" :component-type="ComponentType.Transformer">
                                <ConditionStatusAddRecommendationModal :component-type="ComponentType.Transformer" :component-id="transformerId">
                                    <QueryLoaderGuard :is-pending="isPending" :is-error="isError">
                                        <div class="condition-status-content">
                                            <div class="condition-status-content__master-data-latest-grid">
                                                <MasterDataPanel :transformer-id="transformerId" />
                                                <div class="condition-status-content__master-data-latest-grid__latest-reports">
                                                    <EditNextReportDateModal :component-id="transformerId">
                                                        <ConditionStatusLatestReportsSection :component-id="transformerId" :component-type="ComponentType.Transformer" />
                                                    </EditNextReportDateModal>
                                                </div>
                                            </div>
                                            <ConditionStatusRecommendationSection v-if="userPermissions.canViewTransformerRecommendations" v-model:collapsed="collapsedState.recommendations" :component-id="transformerId" :component-type="ComponentType.Transformer" :headline="t('transformers.conditionStatus.content.recommendations.title')" />
                                            <template v-if="!isSectionHidden">
                                                <ConditionStatusAnalysesSection v-model:collapsed="collapsedState.oilAnalyses" />
                                                <ConditionStatusAnalysisCommentsSection v-model:collapsed="collapsedState.analysisComments" :component-id="transformerId" :component-type="ComponentType.Transformer" :headline="t('transformers.conditionStatus.content.comments.headline')"/>
                                                <ConditionStatusOilHistoricalTrendsSection v-model:collapsed="collapsedState.oilHistoricalTrends" />
                                            </template>
                                            <ConditionStatusVisualInspectionsSection v-model:collapsed="collapsedState.visualInspections" />
                                            <ConditionStatusComponentCommentsSection v-model:collapsed="collapsedState.componentComments" :component-id="transformerId" :component-type="ComponentType.Transformer"/>
                                        </div>
                                    </QueryLoaderGuard>
                                </ConditionStatusAddRecommendationModal>
                            </ConditionStatusEditRecommendationModal>
                        </ConditionStatusViewRecommendationModal>
                    </SystemStatusDescriptionModal>
                </ComponentCommentModal>
            </ConditionStatusCommentModal>
        </ConditionStatusOverrideModal>
    </OverrideReportStatusModal>
</template>

<script setup lang="ts">
import ConditionStatusLatestReportsSection from '@/pages/common/component-status/ConditionStatusLatestReportsSection.vue'
import ConditionStatusAnalysesSection from '@/pages/transformers/condition-status/sections/analyses/ConditionStatusAnalysesSection.vue'
import ConditionStatusOverrideModal from '@/components/modals/override/ConditionStatusOverrideModal.vue'
import ConditionStatusOilHistoricalTrendsSection from '@/pages/transformers/condition-status/sections/oil-historical-trends/ConditionStatusOilHistoricalTrendsSection.vue'
import ConditionStatusVisualInspectionsSection from '@/pages/transformers/condition-status/sections/visual-inspections/ConditionStatusVisualInspectionsSection.vue'
import ConditionStatusCommentModal from '@/components/modals/comment/ConditionStatusCommentModal.vue'
import ComponentCommentModal from '@/components/comments/component-comment/ComponentCommentModal.vue'
import ConditionStatusAnalysisCommentsSection from '@/components/condition-status/sections/comments/ConditionStatusAnalysisCommentsSection.vue'
import ConditionStatusComponentCommentsSection from '@/components/condition-status/sections/comments/ConditionStatusComponentCommentsSection.vue'
import { provideTransformerId } from '@/composables/useTransformerId'
import { computed, toRefs, watch } from 'vue'
import { usePermissions } from '@/composables/usePermissions'
import ConditionStatusRecommendationSection from '@/components/condition-status/sections/recommendations/ConditionStatusRecommendationSection.vue'
import ConditionStatusAddRecommendationModal from '@/components/modals/recommendation/add-recommendation/ConditionStatusAddRecommendationModal.vue'
import ConditionStatusEditRecommendationModal from '@/components/modals/recommendation/edit-recommendation/ConditionStatusEditRecommendationModal.vue'
import ConditionStatusViewRecommendationModal from '@/components/modals/recommendation/view-recommendation/ConditionStatusViewRecommendationModal.vue'
import SystemStatusDescriptionModal from '@/pages/transformers/condition-status/modals/recommendation/system-status-description/SystemStatusDescriptionModal.vue'
import MasterDataPanel from '@/pages/transformers/condition-status/sections/master-data/MasterDataPanel.vue'
import { useExportStateStore } from '@/store/exportStateStore'
import { useConditionStatusCollapseStore } from '@/store/conditionStatusCollapseStore'
import { useRoute, useRouter } from 'vue-router'
import { useTransformerDetailsQuery } from '@/api/queries/transformers/useTransformerDetailsQuery'
import { CONSTRUCTION_TYPE, COUNTRY } from '@/utils/master-data-utils'
import QueryLoaderGuard from '@/components/guards/QueryLoaderGuard.vue'
import { useI18n } from 'vue-i18n'
import { ComponentType } from '@/configuration/component/component-types'
import OverrideReportStatusModal from '@/components/modals/override-report-status/OverrideReportStatusModal.vue'
import { storeToRefs } from 'pinia'
import EditNextReportDateModal from '@/components/modals/edit-next-report-date/EditNextReportDateModal.vue'

const route = useRoute()
const router = useRouter()

const { t } = useI18n()
const { userPermissions } = usePermissions()

const conditionStatusCollapseStore = useConditionStatusCollapseStore()
const { collapsedState } = storeToRefs(conditionStatusCollapseStore)

const exportStateStore = useExportStateStore()

const props = defineProps<{
    transformerId: string
}>()

const { transformerId } = toRefs(props)

const { data, isPending, isError } = useTransformerDetailsQuery(transformerId)

const isSectionHidden = computed(() => {
    return data.value?.transformerTypeEnumSid === CONSTRUCTION_TYPE.TROCKEN && data.value?.country === COUNTRY.Germany
})

const processScrollState = () => {
    if (!route.query.focused || isNaN(Number(route.query.focused))) {
        return
    }

    const focusedState = Number(route.query.focused)
    conditionStatusCollapseStore.setCollapseScrollState(focusedState)

    router.replace({
        query: {},
    })
}

provideTransformerId(computed(() => props.transformerId))

watch(
    () => props.transformerId,
    () => {
        exportStateStore.resetConditionStatusExportParameters()
        processScrollState()
    },
    { immediate: true },
)
</script>

<style scoped lang="scss">
.condition-status-content {
    display: flex;
    flex-direction: column;

    gap: $spacing-m;

    &__master-data-latest-grid {
        display: flex;
        gap: $spacing-m;

        &__latest-reports {
            flex: 1;
        }
    }
}
</style>
